<template>
  <div class="pageRoot linkListPage genericResourceModule">
    <div class="pageShell" ref="pageCard">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <link-list-filter-panel
                profile="aiAssistant"
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                  <span v-if="multipleSelection.length" class="scrollControlBar__selected">
                    · 已选 {{ multipleSelection.length }}
                  </span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    :layout="controlBarDisplayLayout"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="multipleSelection.length"
                    @open-filter="openFilterDrawer"
                    @batch-tag="openBatchTagDialog('page')"
                    @export-selected="exportExcel"
                    @clear-selection="clearSelection"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      v-if="controlBarShowScrolledInfo"
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="multipleSelection.length"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right">
                    <control-bar-display
                      v-if="controlBarShowScrolledActions"
                      part="actions"
                      :layout="controlBarDisplayLayout"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :selection-count="multipleSelection.length"
                      @batch-tag="openBatchTagDialog('page')"
                      @export-selected="exportExcel"
                      @clear-selection="clearSelection"
                    />
                  </div>
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  :row-key="getRowKey"
                  @row-click="handleRowClick"
                  @selection-change="handleSelectionChange"
                >
                  <el-table-column type="selection" :width="aiTableColWidth.selection" align="center" :reserve-selection="true" />
                  <el-table-column label="运营名称" prop="resourceName" :width="aiTableColWidth.name" show-overflow-tooltip>
                    <template slot-scope="scope">
                      <word-copy>{{ scope.row.resourceName }}</word-copy>
                    </template>
                  </el-table-column>
                  <el-table-column label="角色名称" prop="roleName" :width="aiTableColWidth.roleName" show-overflow-tooltip />
                  <el-table-column label="头像" :width="aiTableColWidth.avatar" align="center">
                    <template slot-scope="scope">
                      <el-image
                        v-if="scope.row.thumbUrl"
                        class="aiListPreview"
                        :src="scope.row.thumbUrl"
                        :preview-src-list="[scope.row.thumbUrl]"
                        fit="cover"
                      />
                      <span v-else class="cellMuted">—</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="分类"
                    align="center"
                    header-align="center"
                    min-width="180"
                    :width="aiTableColWidth.category"
                    show-overflow-tooltip
                    class-name="categoryColumn"
                  >
                    <template slot-scope="scope">
                      <div
                        v-if="scope.row.categorySimpleVOList && scope.row.categorySimpleVOList.length"
                        class="categoryCell"
                      >
                        <router-link
                          v-for="item in getVisibleCategories(scope.row.categorySimpleVOList)"
                          :key="item.id"
                          :to="{ path: '/collection/classify/info/' + item.id }"
                          class="categoryCell__link"
                        >
                          <el-tag size="mini" effect="plain" class="categoryTag">{{
                            item.name
                          }}</el-tag>
                        </router-link>
                        <el-tag
                          v-if="getHiddenCategoryCount(scope.row.categorySimpleVOList) > 0"
                          size="mini"
                          effect="plain"
                          class="categoryTag"
                        >
                          +{{ getHiddenCategoryCount(scope.row.categorySimpleVOList) }}
                        </el-tag>
                      </div>
                      <span v-else class="cellEmptyPill">未关联</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="关联解锁" :width="aiTableColWidth.lock" show-overflow-tooltip>
                    <template slot-scope="scope">
                      <button
                        type="button"
                        class="aiListLockBtn"
                        @click.stop="showLockType(scope.row.id)"
                      >
                        {{ scope.row.defaultLockGroupName || '查看' }}
                      </button>
                    </template>
                  </el-table-column>
                  <el-table-column label="状态" :width="aiTableColWidth.status" align="center">
                    <template slot-scope="scope">
                      <span
                        class="statusPill"
                        :class="'statusPill--' + getStatusPillVariant(scope.row.onOrOff)"
                      >
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ getStatusLabel(scope.row.onOrOff) }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column label="更新时间" prop="updateTime" :width="aiTableColWidth.time" />
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    :width="aiTableColWidth.action"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          :disabled="scope.row.onOrOff === 10"
                          @click.stop="goInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-edit"
                          :disabled="scope.row.onOrOff === 10"
                          @click.stop="goEdit(scope.row.id)"
                        >
                          编辑
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-document-copy"
                          @click.stop="copyItem(scope.row.id)"
                        >
                          复制
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost rowActionBtn--danger"
                          icon="el-icon-delete"
                          v-check-role
                          :disabled="scope.row.onOrOff === 10"
                          @click.stop="deleteResourceRow(scope.row)"
                        >
                          删除
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-user" /></div>
                <p class="tableEmpty__title">暂无 Ai 角色</p>
                <p class="tableEmpty__desc">调整筛选条件或创建新角色。</p>
                <el-button type="primary" size="small" @click="$router.push('/resource/aiAssistant/create')">
                  创建角色
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button type="button" class="filterDrawerPanel__closeBtn" @click="closeFilterDrawer">
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <link-list-filter-panel
                profile="aiAssistant"
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第 {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog title="关联解锁方式" :visible.sync="dialogVisible" width="480px" top="25vh">
      <div v-loading="dialogLoading">
        <el-form disabled :model="form" label-position="top" size="mini">
          <el-form-item label="默认解锁方式">
            <el-input v-model="form.defaultUnlockType" />
          </el-form-item>
          <el-form-item label="数据集解锁方式(选填)">
            <div
              v-for="(item, index) in form.data_unlock_type_list"
              :key="index"
              class="aiListUnlockRow"
            >
              <el-input v-model="item.lockGroupName" />
              <el-input v-model="item.dataSetName" />
            </div>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer">
        <el-button size="mini" @click="handleClose">关闭</el-button>
      </span>
    </el-dialog>

    <upload-excel-component
      :on-success="uploadExcelSuccess"
      :centerDialogVisible.sync="uploadExcelDialog"
    />

    <batch-tag-dialog
      :visible.sync="batchTagVisible"
      :selection="multipleSelection"
      :filters="batchTagFilters"
      :resource-type="resourceType"
      list-profile="aiAssistant"
      :category-options="categoryOptions"
      :data-options="dataOptions"
      :status-options="wallpaperStatusOptions"
      :type-options="aiAssistantTypeOptions"
      :default-tab="batchTagDefaultTab"
      @success="handleBatchTagSuccess"
      @clear-list-selection="clearSelection"
    />
  </div>
</template>

<script>
import {
  getAiAssistantList,
  getAiAssistantById,
  copyAiAssistant,
  batchAddAiAssistant,
  batchDeleteResource,
  deleteAiAssistantById,
} from '../../../api/aiAssistant'
import {
  exportResourceList,
  importResourceList,
  getAllCategory,
} from '../../../api/comment'
import UploadExcelComponent from '../../../components/uploadExcel/index.vue'
import BatchTagDialog from '../genericResourceManage/batchTagDialog.vue'
import ControlBarDisplay from '../genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../genericResourceManage/ControlBarNav.vue'
import LinkListFilterPanel from '../genericResourceManage/LinkListFilterPanel.vue'
import linkListScrollControlMixin from '../genericResourceManage/linkListScrollControlMixin'
import { getStatusLabel, getStatusPillVariant } from '../genericResourceManage/batchTag/resourceDisplayHelper'
import { normalizeAiAssistantList, toAiAssistantListQueryParams } from './aiAssistantAdapter'
import { computeAiAssistantTableColumnWidths } from './aiAssistantTableLayout'
import { AI_ASSISTANT_ASSET_TYPE, AI_ASSISTANT_CLASSIFY_TYPE, AI_ASSISTANT_LIST_RESOURCE_TYPE } from './aiAssistantConstants'
import { getAllDataList } from '../../../api/classifyManage'

export default {
  name: 'AiAssistantList',
  mixins: [linkListScrollControlMixin],
  components: {
    UploadExcelComponent,
    BatchTagDialog,
    ControlBarDisplay,
    ControlBarNav,
    LinkListFilterPanel,
  },
  data() {
    return {
      resourceName: '',
      roleNameFilter: '',
      wallpaperStatus: '',
      dataSetId: '',
      dataOptions: [],
      wallpaperStatusOptions: [
        { label: '上线', value: '1' },
        { label: '下线', value: '0' },
        { label: '资源处理中', value: '10' },
        { label: '资源处理失败', value: '11' },
      ],
      tableData: [],
      currentPage: 1,
      currentPageSize: 20,
      total: 0,
      loading: false,
      categoryId: '',
      categoryOptions: [],
      uploadExcelDialog: false,
      multipleSelection: [],
      currentPageActive: [],
      resourceType: AI_ASSISTANT_LIST_RESOURCE_TYPE,
      form: {},
      dialogVisible: false,
      dialogLoading: false,
      batchTagVisible: false,
      batchTagDefaultTab: 'page',
      isSyncingTableSelection: false,
    }
  },
  computed: {
    aiTableColWidth() {
      return computeAiAssistantTableColumnWidths(this.tableContainerWidth)
    },
    activeScopeFilters() {
      const chips = []
      const roleName = String(this.roleNameFilter || '').trim()
      if (roleName) {
        chips.push({ key: 'roleName', label: '角色名称', value: roleName })
      }
      if (this.categoryId !== '' && this.categoryId != null) {
        chips.push({
          key: 'categoryId',
          label: '资源分类',
          value: this.resolveCategoryLabel(this.categoryId),
        })
      }
      if (this.dataSetId !== '' && this.dataSetId != null) {
        chips.push({
          key: 'dataSetId',
          label: '数据集',
          value: this.resolveDataSetLabel(this.dataSetId),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters.map((item) => `${item.label}: ${item.value}`).join(' · ')
    },
    aiAssistantTypeOptions() {
      return [AI_ASSISTANT_ASSET_TYPE]
    },
    batchTagFilters() {
      return {
        resourceName: this.resourceName,
        roleName: this.roleNameFilter,
        wallpaperStatus: this.wallpaperStatus,
        categoryType: this.categoryId,
        dataSetId: this.dataSetId,
      }
    },
    activeFilterCount() {
      return [
        this.resourceName,
        this.roleNameFilter,
        this.categoryId,
        this.wallpaperStatus,
        this.dataSetId,
      ].filter((value) => value !== '' && value != null).length
    },
    filterPanelProps() {
      return {
        profile: 'aiAssistant',
        resourceName: this.resourceName,
        roleName: this.roleNameFilter,
        categoryType: this.categoryId,
        wallpaperStatus: this.wallpaperStatus,
        dataSetId: this.dataSetId,
        activeFilterCount: this.activeFilterCount,
        total: this.listTotalCount,
        categoryOptions: this.categoryOptions,
        dataOptions: this.dataOptions,
        wallpaperStatusOptions: this.wallpaperStatusOptions,
      }
    },
    filterPanelListeners() {
      return {
        'update:resourceName': (value) => { this.resourceName = value },
        'update:roleName': (value) => { this.roleNameFilter = value },
        'update:categoryType': (value) => { this.categoryId = value },
        'update:wallpaperStatus': (value) => { this.wallpaperStatus = value },
        'update:dataSetId': (value) => { this.dataSetId = value },
        search: this.search,
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetFilters,
        'go-create': () => this.$router.push('/resource/aiAssistant/create'),
        'open-upload-excel': () => { this.uploadExcelDialog = true },
        'export-excel': this.exportExcel,
        'open-batch-upload': this.batchUploadExcelData,
        'batch-tag': () => {
          this.openBatchTagDialog('page')
        },
        'batch-delete': this.batchDelete,
      }
    },
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
  },
  watch: {
    tableContainerWidth() {
      this.scheduleTableLayout()
    },
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    'multipleSelection.length'() {
      if (this.batchTagVisible) {
        return
      }
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        if (this.filterDrawerOpen) {
          this.syncDrawerLayout()
        }
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getCategory()
    this.getAllDataSet()
    this.getList()
  },
  methods: {
    getStatusLabel,
    getStatusPillVariant,
    /** 分类列最多展示 2 个 tag，其余折叠为 +N（与通用资源列表一致） */
    getVisibleCategories(list) {
      return (list || []).slice(0, 2)
    },
    getHiddenCategoryCount(list) {
      return Math.max(0, (list || []).length - 2)
    },
    resetFilters() {
      this.resourceName = ''
      this.roleNameFilter = ''
      this.categoryId = ''
      this.wallpaperStatus = ''
      this.dataSetId = ''
      this.search()
    },
    getAllDataSet() {
      getAllDataList().then((res) => {
        if (res.success) {
          this.dataOptions = res.data
        }
      })
    },
    batchDelete() {
      const keyList = this.multipleSelection.map((item) => item.id)
      if (!keyList.length) {
        this.$message.warning('请先勾选要删除的资源')
        return
      }
      this.$confirm('是否确定批量删除？', '提示', { type: 'warning' })
        .then(() => batchDeleteResource({ ids: keyList }))
        .then((res) => {
          if (res.success) {
            this.$message.success('批量删除成功')
            this.clearSelection()
            this.getList()
          }
        })
        .catch(() => {})
    },
    deleteResourceRow(row) {
      if (!row || row.id == null) {
        this.$message.warning('资源 ID 缺失，无法删除')
        return
      }
      this.$confirm('此操作将删除该资源，是否继续？', '提示', { type: 'warning' })
        .then(() => deleteAiAssistantById({ id: row.id }))
        .then((res) => {
          if (res && res.success) {
            this.$message.success('删除成功')
            this.multipleSelection = this.multipleSelection.filter(
              (item) => item.key !== row.key
            )
            this.getList()
          }
        })
        .catch(() => {})
    },
    openBatchTagDialog(defaultTab) {
      this.batchTagDefaultTab = defaultTab
      this.batchTagVisible = true
    },
    clearSelection() {
      this.multipleSelection = []
      if (this.$refs.multipleTable) {
        this.$refs.multipleTable.clearSelection()
      }
    },
    handleBatchTagSuccess() {
      this.clearSelection()
      this.getList()
    },
    batchUploadExcelData() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.xlsx,.xls,.csv'
      input.onchange = (e) => {
        const file = e.target.files[0]
        const form = new FormData()
        form.append('file', file)
        batchAddAiAssistant(form)
          .then((res) => {
            if (res && res.success) {
              this.$message.success('导入成功')
              this.getList()
            } else {
              this.$message.error((res && res.errMessage) || '导入失败')
            }
          })
          .catch((err) => {
            this.$message.error(err || '导入失败')
          })
      }
      input.click()
    },
    getRowKey(row) {
      return row.id
    },
    getCategory() {
      getAllCategory('', AI_ASSISTANT_CLASSIFY_TYPE).then((res) => {
        if (res.success) {
          this.categoryOptions = res.data
        }
      })
    },
    getList() {
      this.loading = true
      getAiAssistantList(
        toAiAssistantListQueryParams({
          currentPage: this.currentPage,
          currentPageSize: this.currentPageSize,
          resourceName: this.resourceName,
          roleNameFilter: this.roleNameFilter,
          categoryId: this.categoryId,
          wallpaperStatus: this.wallpaperStatus,
          dataSetId: this.dataSetId,
        })
      )
        .then((res) => {
          if (res.success) {
            this.total = res.data.total
            this.tableData = normalizeAiAssistantList(res.data.wallpaperVOList)
            this.checkedElementCheck()
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => this.getList())
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    showLockType(id) {
      this.dialogVisible = true
      this.dialogLoading = true
      getAiAssistantById({ id })
        .then((res) => {
          if (res.success) {
            this.form.defaultUnlockType = res.data.defaultLockGroupName
            this.form.data_unlock_type_list = res.data.wallpaperLockGroupVOList
          }
        })
        .finally(() => {
          this.dialogLoading = false
        })
    },
    handleClose() {
      this.dialogVisible = false
      this.form = {}
    },
    handleRowClick(row, _column, event) {
      if (!event || !event.target) {
        return
      }
      const target = event.target
      if (
        target.closest(
          'a, button, .el-button, .el-image, img, .el-checkbox, .lockCell, .el-tag'
        )
      ) {
        return
      }
      const table = this.$refs.multipleTable
      if (!table) {
        return
      }
      table.toggleRowSelection(row)
    },
    goInfo(id) {
      this.$router.push(`/resource/aiAssistant/info/${id}`)
    },
    goEdit(id) {
      this.$router.push(`/resource/aiAssistant/edit/${id}`)
    },
    copyItem(id) {
      this.$confirm('此操作将复制该角色, 是否继续?', '提示', { type: 'warning' })
        .then(() => copyAiAssistant({ id }))
        .then((res) => {
          if (res.success) {
            this.getList()
            this.$message.success('复制成功')
          }
        })
        .catch(() => {})
    },
    handleSelectionChange(val) {
      let newItem = []
      const deleteItem = []
      const keys = this.multipleSelection.map((e) => e.key)
      const valKeys = val.map((e) => e.key)
      val.forEach((e) => {
        if (!keys.includes(e.key)) {
          newItem.push(e)
        }
      })
      this.currentPageActive.forEach((e) => {
        if (!valKeys.includes(e.key)) {
          deleteItem.push(e.key)
        }
      })
      const filtered = this.multipleSelection.filter((value) => !deleteItem.includes(value.key))
      filtered.push(...newItem)
      this.multipleSelection = filtered
      setTimeout(() => {
        this.currentPageActive = val
      }, 100)
    },
    exportExcel() {
      const keyList = this.multipleSelection.map((item) => item.key)
      exportResourceList({
        keySet: keyList,
        resourceType: this.resourceType,
        wallpaperName: this.resourceName,
        name2: this.roleNameFilter,
        categoryId: this.categoryId,
      })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' })
          const href = window.URL.createObjectURL(blob)
          const downloadElement = document.createElement('a')
          downloadElement.href = href
          // CORS 未暴露 Content-Disposition 时 header 可能缺失，避免运行时崩溃
          const disposition =
            (res.headers &&
              (res.headers['content-disposition'] || res.headers['Content-Disposition'])) ||
            ''
          const filenameMatch = String(disposition).match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)
          const rawName = filenameMatch ? filenameMatch[1].replace(/"/g, '').trim() : ''
          downloadElement.download = rawName ? decodeURIComponent(rawName) : 'ai-assistant-export.xls'
          document.body.appendChild(downloadElement)
          downloadElement.click()
          document.body.removeChild(downloadElement)
          window.URL.revokeObjectURL(href)
          setTimeout(() => {
            this.multipleSelection = []
            if (this.$refs.multipleTable) {
              this.$refs.multipleTable.clearSelection()
            }
          }, 100)
        })
        .catch((err) => {
          this.$message.error(err || '导出失败')
        })
    },
    checkedElementCheck() {
      const keyItemList = []
      this.multipleSelection.forEach((element) => {
        const keyItem = []
        this.tableData.forEach((item) => {
          if (item.key === element.key) {
            keyItem.push(item)
          }
        })
        keyItemList.push(...keyItem)
      })
      if (keyItemList.length > 0) {
        keyItemList.forEach((e) => {
          setTimeout(() => {
            if (this.$refs.multipleTable) {
              this.$refs.multipleTable.toggleRowSelection(e, true)
            }
          }, 100)
        })
      }
      this.currentPageActive = keyItemList
    },
    uploadExcelSuccess({ results, header }, radio, file) {
      if (radio === '1') {
        if (header.includes('key')) {
          this.$message.success('导入成功')
          this.multipleSelection = results
          this.checkedElementCheck()
        } else {
          this.$message.error('必须包含 key 字段')
        }
        return
      }
      const form = new FormData()
      form.append('file', file)
      form.append('resourceType', this.resourceType)
      importResourceList(form)
        .then((res) => {
          if (res.data.success) {
            this.$message.success('导入成功')
            this.getList()
            this.multipleSelection = []
          } else {
            this.$message.error(res.data.errMessage || '导入失败')
          }
        })
        .catch((err) => {
          this.$message.error(err || '导入失败')
        })
    },
  },
}
</script>

<style scoped>
/* 共享壳样式由 index.vue 入口统一引入；此处只保留本页局部类 */
@import '../genericResourceManage/linkTableHybrid.css';
@import '../genericResourceManage/linkSemanticPills.css';
@import '../genericResourceManage/linkActionButtons.css';

.aiListPreview {
  width: 48px;
  height: 48px;
  border-radius: 8px;
}

.categoryCell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.aiListLockBtn {
  border: none;
  background: none;
  color: var(--link-accent);
  cursor: pointer;
  padding: 0;
  font-size: 13px;
}

.aiListUnlockRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
}

.statusPill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px 3px 6px;
  border-radius: 999px;
  font-size: 12px;
}

.statusPill__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
}

.statusPill--online {
  background: rgba(167, 243, 208, 0.4);
  color: #047857;
}

.statusPill--offline {
  background: #f3f0ea;
  color: #78716c;
}

.statusPill--pending {
  background: #fef3c7;
  color: #b45309;
}

.statusPill--failed {
  background: #fee2e2;
  color: #b91c1c;
}

.cellMuted {
  color: var(--link-ink-muted);
}
</style>
